import { useEffect, useRef } from "react";
import { deviceController } from "../AppController";
import { useAppSelector, useAppDispatch } from "./redux";
import {
  setAssignedDevice,
  stopDeviceOnlinePolling,
  setDevicePollingTimedOut,
} from "../redux/Clinic/clinic.slice";

const POLL_INTERVAL_MS = 5_000;
const MAX_DURATION_MS = 60_000; // 1 minute

/**
 * Runs globally (mounted in Mobile layout) to detect when a newly-provisioned
 * Circadia device comes online after Wi-Fi setup.
 *
 * Starts only when Redux `deviceOnlinePolling` is non-null (set by ConnectDevice
 * after provisioning succeeds). Polls every 5 s for up to 2 minutes.
 *
 * On is_online: true  → patches assignedDevice.device_conn_status = 1, stops.
 * On 2-min timeout    → silently stops.
 */
export const useDeviceOnlinePolling = (): void => {
  const dispatch = useAppDispatch();
  const pollingState = useAppSelector(
    (state) => state.clinic.deviceOnlinePolling
  );
  const assignedDevice = useAppSelector((state) => state.clinic.assignedDevice);

  // Use refs to avoid stale closures inside the interval
  const pollingStateRef = useRef(pollingState);
  const assignedDeviceRef = useRef(assignedDevice);
  pollingStateRef.current = pollingState;
  assignedDeviceRef.current = assignedDevice;

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // No active polling request — clear any leftover interval and bail
    if (!pollingState) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const { deviceId, startedAt: _startedAt } = pollingState;

    const poll = async () => {
      const current = pollingStateRef.current;
      if (!current) return;

      // Enforce 2-minute hard cap
      if (Date.now() - current.startedAt >= MAX_DURATION_MS) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        dispatch(stopDeviceOnlinePolling());
        dispatch(setDevicePollingTimedOut(true));
        return;
      }

      try {
        const result = await deviceController.getDeviceConnectionStatus({
          deviceId,
        });

        if (result.success && result.data?.is_online) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;

          // Patch device_conn_status to 1 (online) in Redux
          const current_device = assignedDeviceRef.current;
          if (current_device) {
            dispatch(
              setAssignedDevice({ ...current_device, device_conn_status: 1 })
            );
          }

          dispatch(stopDeviceOnlinePolling());
        }
      } catch {
        // Network error during poll — keep going until timeout
      }
    };

    // Run once immediately, then on interval
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Re-run only when pollingState changes (new poll started or stopped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingState?.deviceId, pollingState?.startedAt]);
};
